import { useEffect, useState } from "react";
import axios from "axios";
import { Camera, X, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isVideo = (url) => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.includes('.mp4') || lower.includes('.mov') || lower.includes('.webm') || lower.includes('video');
};

export default function GalleryPage() {
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [activeAlbum, setActiveAlbum] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(r => {
      setPhotos(r.data);
      const uniqueAlbums = [...new Set(r.data.map(p => p.album || "General"))];
      setAlbums(uniqueAlbums);
    }).catch(() => {});
  }, []);

  const filtered = activeAlbum === "All" ? photos : photos.filter(p => (p.album || "General") === activeAlbum);

  return (
    <div data-testid="gallery-page" className="aged-paper">
      {/* Header */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="segment-label">Footage</div>
            <div className="flex-1 h-[1px] bg-foreground/15" />
          </div>
          <h1 data-testid="gallery-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase mb-4">
            Photo Gallery
          </h1>
          <div className="h-[3px] w-16 gold-accent" />
        </div>
      </section>

      <div className="vintage-divider" />

      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Album filter */}
          {albums.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-10">
              <button
                onClick={() => setActiveAlbum("All")}
                className={`font-heading text-xs uppercase px-5 py-2 transition-colors ${
                  activeAlbum === "All"
                    ? "bg-foreground text-background"
                    : "bg-muted text-foreground hover:bg-foreground hover:text-background"
                }`}
              >
                All
              </button>
              {albums.map(a => (
                <button
                  key={a}
                  data-testid={`album-filter-${a}`}
                  onClick={() => setActiveAlbum(a)}
                  className={`font-heading text-xs uppercase px-5 py-2 transition-colors ${
                    activeAlbum === a
                      ? "bg-foreground text-background"
                      : "bg-muted text-foreground hover:bg-foreground hover:text-background"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="py-20 text-center">
              <Camera size={32} className="mx-auto text-muted-foreground mb-4" />
              <p className="font-heading text-xl text-muted-foreground uppercase mb-2">No Photos Yet</p>
              <p className="font-body text-sm text-muted-foreground">
                Check back after the next show.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filtered.map(photo => (
                <button
                  key={photo.id}
                  data-testid={`gallery-photo-${photo.id}`}
                  onClick={() => setLightbox(photo)}
                  className="relative group overflow-hidden aspect-square bg-[#0D0D0D]"
                >
                  {isVideo(photo.image_url) ? (
                    <>
                      <video src={photo.image_url} className="w-full h-full object-cover" muted />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-black/60 rounded-full w-12 h-12 flex items-center justify-center">
                          <Play size={20} className="text-[#E8DFC8] ml-1" fill="#E8DFC8" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img
                      src={photo.image_url}
                      alt={photo.caption || "Gallery photo"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                    {photo.caption && (
                      <div className="w-full p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                        <p className="font-body text-xs text-[#E8DFC8] text-outline-sm">{photo.caption}</p>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-6 right-6 text-[#E8DFC8] hover:text-white z-10"
            onClick={() => setLightbox(null)}
          >
            <X size={32} />
          </button>
          <div className="max-w-4xl max-h-[85vh] relative" onClick={e => e.stopPropagation()}>
            {isVideo(lightbox.image_url) ? (
              <video
                src={lightbox.image_url}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] mx-auto"
              />
            ) : (
              <img
                src={lightbox.image_url}
                alt={lightbox.caption || ""}
                className="max-w-full max-h-[80vh] object-contain mx-auto"
              />
            )}
            {lightbox.caption && (
              <div className="mt-4 text-center">
                <p className="font-body text-sm text-[#E8DFC8]">{lightbox.caption}</p>
                {lightbox.album && lightbox.album !== "General" && (
                  <p className="font-body text-[10px] text-[#B09030] uppercase tracking-widest mt-1">{lightbox.album}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
